import * as Api from '@ViewModels';
import { useReducer } from 'react';
import { createContentStateWithHtmlStringValue, firstNameHtml } from '../../../../models/UiUtils';

interface ITextingCampaignPageState {
	campaignTitle: string;
	contentEditorState: Api.IRichContentEditorState;
	attachmentFiles: File[];
	storedAttachments: Api.IFileAttachment[];
	sendAttachmentsAsLink: boolean;
}

type Actions =
	| { type: 'setCampaignTitle'; value: string }
	| { type: 'setTextingContentEditorState'; value: Api.IRichContentEditorState }
	| { type: 'addAttachmentFiles'; value: File[] }
	| { type: 'removeAttachmentFile'; value: File }
	| { type: 'setSendAttachmentsAsLink'; value: boolean }
	/**
	 * Replaces all stored attachments at once.
	 * Use when loading attachments from API; use removeStoredAttachment for individual removals.
	 */
	| { type: 'setStoredAttachments'; value: Api.IFileAttachment[] }
	| { type: 'removeStoredAttachment'; value: string };

const reducer = (state: ITextingCampaignPageState, action: Actions): ITextingCampaignPageState => {
	switch (action.type) {
		case 'setCampaignTitle': {
			return {
				...state,
				campaignTitle: action.value,
			};
		}
		case 'setTextingContentEditorState': {
			return {
				...state,
				contentEditorState: action.value,
			};
		}
		case 'addAttachmentFiles': {
			return {
				...state,
				attachmentFiles: state.attachmentFiles.concat(action.value),
			};
		}
		case 'removeAttachmentFile': {
			return {
				...state,
				attachmentFiles: state.attachmentFiles.filter(x => x !== action.value),
			};
		}
		case 'setStoredAttachments': {
			return {
				...state,
				storedAttachments: action.value,
			};
		}
		case 'removeStoredAttachment': {
			return {
				...state,
				storedAttachments: state.storedAttachments.filter(a => a.id !== action.value),
			};
		}
		case 'setSendAttachmentsAsLink': {
			return {
				...state,
				sendAttachmentsAsLink: action.value,
			};
		}
		default:
			throw new Error('Invalid action type');
	}
};

/**
 * Custom hook for managing texting campaign editor state including campaign content,
 * title, and file attachments (both new files and stored attachments).
 *
 * @param initialCampaignTitle - Initial campaign title from draft/template
 * @param initialContentEditorState - Initial rich text content state
 * @param initialAttachmentFiles - Initial file attachments (not yet uploaded)
 * @param initialStoredAttachments - Initial stored attachments (already uploaded to server)
 *
 * @returns Object containing current state and dispatch function for state updates
 *
 * @example
 * const { state, dispatch } = useTextingCampaignEditor({
 *   initialCampaignTitle: 'My Campaign',
 *   initialContentEditorState: contentState,
 * });
 *
 * // Add new attachment files
 * dispatch({ type: 'addAttachmentFiles', value: files });
 *
 * // Remove stored attachment by ID
 * dispatch({ type: 'removeStoredAttachment', value: attachmentId });
 */
export function useTextingCampaignEditor({
	initialCampaignTitle,
	initialContentEditorState,
	initialAttachmentFiles,
	initialStoredAttachments,
	initialSendAttachmentsAsLink,
}: {
	initialCampaignTitle?: string;
	initialContentEditorState?: Api.IRichContentEditorState;
	initialAttachmentFiles?: File[];
	initialStoredAttachments?: Api.IFileAttachment[];
	initialSendAttachmentsAsLink?: boolean;
}) {
	const [state, dispatch] = useReducer(reducer, {
		campaignTitle: initialCampaignTitle ?? '',
		contentEditorState: initialContentEditorState ?? createContentStateWithHtmlStringValue(`Hi ${firstNameHtml}, `),
		attachmentFiles: initialAttachmentFiles ?? [],
		storedAttachments: initialStoredAttachments ?? [],
		sendAttachmentsAsLink: initialSendAttachmentsAsLink ?? false,
	});
	return {
		state,
		dispatch,
	};
}
